'use client';

import { useEffect, useState } from 'react';

import { useTvHeaderContext } from '.';
import { SubscribeEventsMap } from '../../lib/charting_library';
import { useTradingViewDataStorage } from '../misc/data-manager';
import { Button } from './button';

const studyEvents: (keyof SubscribeEventsMap)[] = ['study', 'study_event'];
export const ShortCutStudyMapper: Record<string, { label: string; inputs?: { length: number } }> = {
  'Bollinger Bands': { label: 'BB' },
  'Moving Average': { label: 'MA15', inputs: { length: 15 } },
  'Relative Strength Index': { label: 'RSI' },
};

export const ShortCutStudies = Object.entries(ShortCutStudyMapper).map(([name, values]) => ({ name, ...values }));

export const QuickStudies = () => {
  const tv = useTvHeaderContext();
  const [, setQuickStudies] = useTradingViewDataStorage({ key: 'quickStudies' });
  const [activeStudies, setActiveStudies] = useState<Set<string>>(new Set());

  // Derive button active state from chart's current studies
  useEffect(() => {
    const chart = tv.widget;

    const syncActiveState = () => {
      const studies = tv.activeChart?.getAllStudies() ?? [];
      const names = new Set(studies.filter((s) => s.name in ShortCutStudyMapper).map((s) => s.name));
      setActiveStudies(names);
    };

    studyEvents.forEach((event) => {
      chart?.subscribe(event, syncActiveState);
    });

    const offDataLoaded = tv.on('onFirstDataLoaded', syncActiveState);
    return () => {
      offDataLoaded();
      try {
        studyEvents.forEach((event) => {
          chart?.unsubscribe(event, syncActiveState);
        });
      } catch {
        // ignore internal error
      }
    };
  }, [tv]);

  return (
    <>
      {ShortCutStudies.map((study) => {
        const enabled = activeStudies.has(study.name);
        return (
          <Button
            active={enabled}
            tooltip={study.name}
            key={study.label}
            css={{ textStyle: 'Text/XS/Regular' }}
            onClick={() => {
              if (!tv.enabled || !tv.activeChart) return;

              if (enabled) {
                const chartStudy = tv.activeChart.getAllStudies().find((s) => s.name === study.name);
                if (chartStudy) tv.activeChart.removeEntity(chartStudy.id);
                setQuickStudies((prev) => ({
                  ...prev,
                  [study.name]: { label: study.label, enabled: false, inputs: study.inputs },
                }));
              } else {
                tv.activeChart.createStudy(study.name, false, false, study.inputs);
                setQuickStudies((prev) => ({
                  ...prev,
                  [study.name]: { label: study.label, enabled: true, inputs: study.inputs },
                }));
              }
            }}
          >
            {study.label}
          </Button>
        );
      })}
    </>
  );
};
